import { mobileMediaQuery } from '@caterdesk/ui--theme'
import { Box, Container, Grid as MuiGrid } from '@mui/material'
import type { GetStaticProps, NextPage } from 'next'
import type { AppProps } from 'next/app'
import type React from 'react'
import { useMedia } from 'react-use'
import styled from 'styled-components'

import { useVendorHighlightCategories } from '@/components/page-specific/gm/components/states/useVendorHighlightCategories'
import { useVendorListPageState } from '@/components/page-specific/gm/components/states/useVendorListPageState'
import { useVendorSearchState } from '@/components/page-specific/gm/components/states/vendor-list'
import VendorFiltersMui from '@/components/page-specific/gm/components/vendor-filters/index-mui'
import VendorHeader from '@/components/page-specific/gm/components/vendor-header/overview'
import VendorListHighlights from '@/components/page-specific/gm/components/vendor-list-highlights'
import VendorSearchBar from '@/components/page-specific/gm/components/vendor-search-bar'
import VendorsList from '@/components/page-specific/gm/components/vendors-list'
import withGm from '@/components/page-specific/gm/components/withGm'
import getDomainLangPaths from '@/helpers/getDomainLangPaths'
import { getTenantFromDomain } from '@/helpers/tenants'
import { SPLITS, useFeatureFlag } from '@/helpers/useFeatureFlag'
import { getVendorPermalink } from '@/helpers/vendors'
import { useMixPanel } from '@/helpers/mixpanelConfig'
import { useEffect } from 'react'
import useTenant from '@/hooks/useTenant'
import { useAuthState } from '@/components/page-specific/gm/components/states/auth'

const MainWrapper = styled.div`
  padding: 16px 8px;
  background-color: ${(p) => p.theme.colors.background_lightest};
  & > div {
    row-gap: 0;
  }

  @media ${mobileMediaQuery} {
    padding-top: 0;
  }
`

const Vendors: NextPage = () => {
  const showHighlightsFlagOn = useFeatureFlag(SPLITS.SHOW_HIGHLIGHTS_GM)
  const mixPanel = useMixPanel()
  const tenant = useTenant()
  const auth = useAuthState()
  const {
    called,
    filterOpen,
    toggleFilterOpen,
    search,
    filters,
    searching,
    stats,
    filterCount,
    defaultSearch,
    vendors,
    pageInfo,
  } = useVendorSearchState()
  const { data: highlightsData } = useVendorHighlightCategories()
  const { userBasketSettings, minAllowedDeliveryDate, deliveryTimeSlots } = useVendorListPageState()
  const isMobileScreen = useMedia(mobileMediaQuery, false)

  const showHighlights =
    showHighlightsFlagOn &&
    !filters.cuisineTags?.length &&
    !filters.dietaryTags?.length &&
    !filters.keyDatesTags?.length &&
    !filters.highlightsTags?.length &&
    !filters.occasionTags?.length &&
    !filters.diversityOfOwnershipTags?.length &&
    !filters.search &&
    highlightsData &&
    filters.location &&
    filters.supplierType !== 'pantry'

  useEffect(() => {
    try {
      mixPanel?.track('Catering_Flow', {
        pageType: 'supplierListViewed',
        name: 'Catering - Basket Created Marketplace',
        address:
          userBasketSettings.value.location?.__typename === 'AccountLocation'
            ? userBasketSettings.value.location.address
            : null,
        customerAccount:
          auth.type === 'authenticated'
            ? `${auth.user.account?.id}-${auth.user.account?.name}`
            : 'guest',
        tenantName: tenant.name,
        tenantId: tenant.id,
        userId: auth.type === 'authenticated' ? auth.user?.id : null,
      })
    } catch (e) {
      console.error('Mixpanel tracking failed', e)
    }
  }, [])

  return (
    <Container>
      {!isMobileScreen && <VendorHeader isPantryOnly={filters.supplierType === 'pantry'} />}

      <Box padding={1} sx={{ borderColor: 'divider' }} borderTop={1} borderBottom={1}>
        <VendorSearchBar
          called={called}
          filterCount={filterCount}
          filters={filters}
          search={search}
          toggleFilterOpen={toggleFilterOpen}
          userBasketSettings={userBasketSettings.value}
          minAllowedDate={minAllowedDeliveryDate}
          timeSlots={deliveryTimeSlots}
          onUserBasketSettingsChange={userBasketSettings.set}
        />
      </Box>
      <MainWrapper>
        <MuiGrid container>
          <MuiGrid item md={12} xs={12} paddingBottom={2}>
            {called ? (
              <VendorFiltersMui
                searching={searching}
                stats={stats}
                filters={filters}
                filterOpen={filterOpen}
                toggleFilterOpen={toggleFilterOpen}
                search={search}
              />
            ) : null}
          </MuiGrid>

          <MuiGrid item md={12} xs={12} paddingX={0}>
            {showHighlights && filters.location ? (
              <VendorListHighlights
                location={filters.location}
                search={search}
                highlightCategoryData={
                  highlightsData.vendorHighlightsForLocation.highlightCategories
                }
              />
            ) : null}

            <VendorsList
              filters={filters}
              called={called}
              defaultSearch={defaultSearch}
              vendors={vendors}
              pageInfo={pageInfo}
              search={search}
              searching={searching}
            />
          </MuiGrid>
        </MuiGrid>
      </MainWrapper>
    </Container>
  )
}

type Params = {
  permalink: string
  lang: string
  domain: string
}

export const getStaticProps: GetStaticProps<Record<string, unknown>, Params> = async ({
  params,
}) => {
  const tenant = params?.domain ? getTenantFromDomain(params.domain) : null
  let vendorPermalink = null

  if (tenant?.features?.emma?.defaultSupplierId) {
    vendorPermalink = await getVendorPermalink(tenant.features.emma.defaultSupplierId.toString())
  }

  if (tenant?.isSingleVendor && vendorPermalink) {
    return {
      redirect: {
        permanent: true,
        destination: `/office-catering/vendors/${vendorPermalink}`,
      },
    }
  }

  return { props: {} }
}

export function getStaticPaths() {
  const allPaths = getDomainLangPaths()

  const filteredPaths = allPaths.filter((path) => {
    const tenant = getTenantFromDomain(path.params.domain)

    return !(tenant?.isSingleVendor && tenant?.features?.emma?.defaultSupplierId)
  })

  return { paths: filteredPaths, fallback: 'blocking' }
}

const Component: React.FC<AppProps> = withGm(Vendors)
Component.displayName = 'GM'

export default Component
